<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { toggleMenu, closeMenu } from '@/utils/menuControl'
import { useRequestsStore } from '@/stores/storeRequests'

const storeRequests = useRequestsStore()

// --- Состояния ---
const isMenuOpen = ref(false)
const headerRef = ref(null)
let lastScrollTop = 0
let ticking = false
const marketList = ref([])

// --- Меню ---
const toggleMenuHandler = () => {
	toggleMenu()
	isMenuOpen.value = !isMenuOpen.value
}

const onMenuLinkClick = () => {
	if (isMenuOpen.value) {
		closeMenu()
		isMenuOpen.value = false
	}
}

// --- Ссылки меню ---
const links = {
	about: 'О нас',
	masks: 'Наши маски',
	manufactures: 'Производство',
	reviews: 'Отзывы',
	support: 'Поддержка',
	contact: 'Где нас найти',
}

// --- Скролл ---
const updateHeaderOnScroll = () => {
	const scrollTop = window.scrollY || 0
	const headerEl = headerRef.value
	if (!headerEl) return

	headerEl.classList.toggle('_header-scroll', scrollTop > 0)

	if (scrollTop > 500) {
		const isScrollingDown = scrollTop > lastScrollTop
		headerEl.style.transform = isScrollingDown
			? `translateY(-${headerEl.offsetHeight}px)`
			: 'translateY(0)'
		headerEl.classList.toggle('_header-show', !isScrollingDown)
	} else {
		headerEl.classList.remove('_header-show')
		headerEl.style.transform = 'translateY(0)'
	}

	lastScrollTop = scrollTop
	ticking = false
}

const onScroll = () => {
	if (!ticking) {
		requestAnimationFrame(updateHeaderOnScroll)
		ticking = true
	}
}

// --- Жизненный цикл ---
onMounted(() => window.addEventListener('scroll', onScroll))
onBeforeUnmount(() => window.removeEventListener('scroll', onScroll))

onMounted(async () => {
	const res = await storeRequests.getMarketPlaces()
	// фильтруем только те, где есть url
	marketList.value = res.filter((item) => !!item.url)
})
</script>

<template>
	<header>
		<div class="header" ref="headerRef">
			<div class="header__container">
				<div class="header__wrapper">
					<div class="header__logo">
						<router-link to="/">
							<img loading="lazy" src="@/assets/img/header/logo.png" alt="Логотип" />
						</router-link>
					</div>

					<div class="header__menu menu">
						<button
							type="button"
							class="menu__icon icon-menu"
							@click="toggleMenuHandler"
							aria-label="Открыть меню"
						>
							<span class="menu__icon_line">
								<svg width="50" height="50">
									<use href="#line_burger" />
								</svg>
							</span>
						</button>

						<div class="menu__block">
							<nav class="menu__body">
								<ul class="menu__list">
									<li v-for="(label, key) in links" :key="key">
										<!-- <a href="#" @click.prevent="scrollToSection(key), onMenuLinkClick()">{{ label }}</a> -->
										<router-link
											:to="{ path: '/', hash: `#${key}` }"
											@click="onMenuLinkClick"
										>
											{{ label }}
										</router-link>
									</li>
								</ul>
							</nav>

							<ul class="menu__links">
								<li v-for="item in marketList" :key="item.id">
									<a :href="item.url" target="_blank" rel="noopener">
										<img :src="item.logo" :alt="item.name" />
									</a>
								</li>
							</ul>
						</div>
					</div>
				</div>
			</div>
		</div>
	</header>
</template>

<style lang="scss" scoped>
.header {
	position: relative;
	z-index: 999;
	width: 100%;
	top: 0;
	left: 0;
	padding: 1rem 0;

	// .header__wrapper
	&__wrapper {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	// .header__logo
	&__logo {
		width: 100%;
		max-width: 25.8rem;

		@media (max-width: 991.98px) {
			max-width: 12rem;
		}

		a {
			display: block;
			width: 30rem;
			position: absolute;
			z-index: 10;
			left: 0;
			top: 10rem;

			@media (max-width: 991.98px) {
				width: 12rem;
				top: 1.5rem;
			}
		}

		img {
			width: 100%;
			height: 100%;
			object-fit: contain;
		}
	}

	// .header__menu
	&__menu {
		@media (min-width: 991.98px) {
			display: contents;
		}

		.menu {
			// .menu__icon
			&__icon {
				@media (min-width: 991.98px) {
					display: none;
				}

				@media (max-width: 991.98px) {
					height: 6rem;
					width: 6rem;
					margin-right: -0.5rem;
					flex-shrink: 0;
					position: relative;
					display: block;
					z-index: 10;
					background-image: url('@/assets/img/header/texture_burger.png');
					background-repeat: no-repeat;
					background-position: center;
					background-size: cover;

					&_line {
						position: relative;
						width: 100%;
						height: 100%;
						display: flex;
						align-items: center;
						justify-content: center;
					}
				}

				.menu-open & {
				}
			}

			// .menu__block
			&__block {
				@media (min-width: 991.98px) {
					display: contents;
				}

				@media (max-width: 991.98px) {
					position: fixed;
					top: 0%;
					left: 0;
					width: 100%;
					z-index: 1;
					padding: 13rem 1.5rem 2.5rem;
					max-height: 100dvh;
					min-height: 100dvh;
					overflow-x: hidden;
					overflow-y: auto;
					transform: translateY(-100%);
					transition: transform 0.6s cubic-bezier(0.77, 0, 0.175, 1) 0s;
					background-image: url('@/assets/img/page_bg.jpg');
					background-size: cover;
					display: flex;
					flex-direction: column;
					justify-content: space-between;

					.menu-open & {
						transform: translateY(0%);
					}
				}
			}

			// .menu__body
			&__body {
			}

			// .menu__list
			&__list {
				@media (min-width: 991.98px) {
					display: flex;
					align-items: center;
					gap: 3rem;

					@media (max-width: 1169.98px) {
						gap: 2rem;
					}
				}

				li {
					@media (max-width: 991.98px) {
						&:not(:last-child) {
							border-bottom: 1px solid rgba(255, 255, 255, 0.2);
						}
					}

					a,
					span {
						display: block;
						font-family: var(--second-font);
						font-weight: 600;
						font-size: 2.4rem;
						// line-height: 2.8rem;
						line-height: 116%;
						cursor: pointer;
						transition: all 0.3s ease 0s;

						@media (max-width: 1169.98px) {
							font-size: 2rem;
						}

						@media (max-width: 991.98px) {
							font-weight: 700;
							font-size: 2.8rem;
							text-transform: uppercase;
							padding: 1.9rem 0;
						}

						@media (any-hover: hover) {
							&:hover {
								transform: rotate(-3deg);
								color: var(--primary-color);
							}
						}
					}
				}
			}

			// .menu__links
			&__links {
				display: flex;
				align-items: center;
				gap: 0.8rem;

				@media (max-width: 991.98px) {
					justify-content: center;
					margin-top: 1.2rem;
				}

				li {
					a {
						display: block;
						width: 8rem;
						height: 8rem;

						@media (any-hover: hover) {
							&:hover {
								svg {
									transform: rotate(-6deg);
								}
							}
						}

						svg {
							width: 100%;
							height: 100%;
							transition: all 0.3s ease 0s;
						}
					}
				}
			}
		}
	}
}
</style>
